import { SeatingData } from '@maverick/entity';
import { useInput } from '@maverick/hooks';
import { DropdownItem, Holder, Icon, IconMapPin } from '@maverick/ui';
import { CmsDropdown } from 'components/cms/CmsDropdown/CmsDropdown';
import { format } from 'date-fns';
import { FC, useState, useEffect } from 'react';
import { SeatingManager } from 'src/manager/Seating.manager';
import { SeatingModal } from '../SeatingModal';
import { useRouter } from 'next/router';
import * as Styled from '../Seating.styled';
import { GaEvent, handleFormatGuestsAndTimeSelections } from '@maverick/utils';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { SeatingStepsReduxProps } from '.';

const guestsOptions = [
	{ value: '2', text: '2 Guests' },
	{ value: '3', text: '3 Guests' },
	{ value: '4', text: '4 Guests' },
	{ value: '5', text: '5 Guests' },
	{ value: '6', text: '6 Guests' },
];

interface MainFromProps extends SeatingStepsReduxProps {
	onNextClick: () => void;
}

export const MainForm: FC<MainFromProps> = ({ seatingData, setSeatingData, onNextClick }) => {
	const [timeSlots, setTimeSlots] = useState<Array<DropdownItem>>([]);
	const guests = useInput<string>(seatingData.Guests ? String(seatingData.Guests) : guestsOptions[0].value, {
		label: 'How many guests?',
	});
	const timeSlot = useInput<string>('', {
		label: 'What time?',
	});
	const [error, setError] = useState<boolean>(false);
	const router = useRouter();
	const { sitecoreContext } = useSitecoreContext();

	useEffect(() => {
		GaEvent.GetOnTheListStart(Number(seatingData.LocationId));
	}, []);

	useEffect(() => {
		handleAvailableTimes();
	}, [seatingData, guests.value]);

	useEffect(() => {
		if (timeSlots.length) {
			if (seatingData.TimeSlot && timeSlots.find((ts) => ts.value === seatingData.TimeSlot)) {
				timeSlot.setValue(seatingData.TimeSlot);
			} else {
				timeSlot.setValue(timeSlots[0].value);
			}
		}
	}, [timeSlots]);

	const handleAvailableTimes = async () => {
		seatingData.Guests = Number(guests.value);

		timeSlot.setDisabled(true).setLoading(true).setValue('');
		const result = await SeatingManager.GetAvailableTimes(seatingData);
		timeSlot.setDisabled(false).setLoading(false);

		if (!result) {
			setError(true);

			GaEvent.ErrorView(
				'Wait list service is unavailable at the moment. Please try again at a later time.',
				sitecoreContext.route?.displayName
			);

			return;
		}

		const slots = result.map((r) => {
			const date = SeatingData.ParseTimeSlot(r);
			const text = format(date, 'h:mm aaa');

			return { value: r, text };
		});

		setTimeSlots(slots);
	};

	const handleNext = () => {
		if (!timeSlot.value) return;

		const guestNumber = Number(guests.value);
		seatingData.Guests = guestNumber;
		seatingData.Day = new Date().toISOString().split('T')[0];
		seatingData.TimeSlot = timeSlot.value;
		setSeatingData(seatingData);

		const guestsAndSelectedTime = handleFormatGuestsAndTimeSelections(guestNumber, seatingData.Datetime);
		GaEvent.GetOnTheListNextStepButton(Number(seatingData.LocationId), guestsAndSelectedTime);

		onNextClick();
	};

	return (
		<>
			<Holder>
				<h1>Get On The List</h1>
				<Styled.Location>
					<div>
						<Icon icon={IconMapPin} customSize />
					</div>
					<span>{seatingData.LocationName}</span>
				</Styled.Location>

				<Styled.Inline>
					<CmsDropdown handler={guests} items={guestsOptions} />
					<CmsDropdown handler={timeSlot} items={timeSlots} />
					<Styled.Button onClick={handleNext}>Next step</Styled.Button>
				</Styled.Inline>

				<Styled.Info>
					{!!seatingData.LocationPhoneNumber && (
						<>
							For parties of 7 or more, please call us at{' '}
							<a href={`tel:${seatingData.LocationPhoneNumber}`}>{seatingData.LocationPhoneNumber}</a>.
							<br />
						</>
					)}
					Please Note: This is not a reservation. You will be added to the current wait list. You may have a
					short wait once you arrive while we prepare your table.
				</Styled.Info>
			</Holder>

			{error && (
				<SeatingModal>
					<h2>Error</h2>
					<p>Wait list service is unavailable at the moment. Please try again at a later time.</p>
					<Styled.Button onClick={() => router.push('/')}>Ok</Styled.Button>
				</SeatingModal>
			)}
		</>
	);
};
